import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSession } from '../Utils/SessionContext';
import { API_ENDPOINTS } from '../Utils/ApiConfig';
import toast from 'react-hot-toast';

const EvaluationSettings = ({ evaluationId, evaluationData, onRefresh }) => {
  const [loading, setLoading] = useState({
    delete: false,
    save: false
  });
  
  // Local state for configuration changes
  const [configChanges, setConfigChanges] = useState({
    name: evaluationData?.name || '',
    step_upload_resume: evaluationData?.step_upload_resume || false,
    step_video_intro: evaluationData?.step_video_intro || false,
    step_questionnaire: evaluationData?.step_questionnaire || false,
    step_challenge: evaluationData?.step_challenge || false,
    is_default: evaluationData?.is_default || false
  });
  
  const { user } = useSession();
  const navigate = useNavigate();

  // Console log evaluationData for debugging
  console.log('EvaluationSettings evaluationData:', evaluationData);

  // Update local state when a field changes
  const handleFieldChange = (field, value) => {
    setConfigChanges(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Placeholder: Save all configuration changes
  const handleSaveConfiguration = () => {
    console.log('Saving configuration changes:', configChanges);
    toast.success('Configuration saved (placeholder)');
    // TODO: Implement API call to update all configuration fields at once
  };

  // Generic function to call the API
  const callEvaluationSettingsAPI = async (action, data = {}) => {
    try {
      if (!user) {
        throw new Error('User session not available. Please log in again.');
      }

      const payload = {
        action: action,
        data: data,
        evaluation_id: evaluationId
      };

      const response = await fetch(API_ENDPOINTS.EDIT_EVALUATION_SETTINGS, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${user.access_token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const error = new Error(`Error: ${response.status} ${response.statusText}`);
        error.status = response.status;
        throw error;
      }

      const result = await response.json();
      console.log(`${action} result:`, result);
      
      return result;
    } catch (err) {
      console.error(`Error with ${action}:`, err);
      
      if (err.status === 401) {
        throw new Error('Your session has expired. Please log in again.');
      } else {
        throw new Error(`Failed to ${action.replace('_', ' ')}. Please try again.`);
      }
    }
  };

  // Delete Evaluation handler
  const handleDeleteEvaluation = async () => {
    if (!window.confirm('Are you sure you want to delete this evaluation? This action cannot be undone.')) {
      return;
    }

    setLoading(prev => ({ ...prev, delete: true }));
    
    try {
      await callEvaluationSettingsAPI('delete_evaluation', {
        evaluation_id: evaluationId
      });
      
      // Navigate to evaluations page and refresh to close panels
      window.location.href = '/evaluations';
    } catch (error) {
      console.error('Delete evaluation error:', error);
      toast.error(error.message || 'Failed to delete evaluation');
    } finally {
      setLoading(prev => ({ ...prev, delete: false }));
    }
  };

  return (
    <div>
      {/* Tab-related header */}
      <div className="bg-white shadow-sm rounded-lg p-6 mb-6 border border-gray-200">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Evaluation Settings</h2>
          <p className="text-gray-600 mt-2 text-lg">
            Manage this evaluation and its components.
          </p>
        </div>
      </div>

      {/* Settings Content */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left Column - Configuration and Danger Zone */}
        <div className="space-y-6">
          {/* Configuration */}
          <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Configuration</h3>
              <span className="px-3 py-1 text-xs font-semibold text-white bg-orange-500 rounded-full">
                Coming Soon
              </span>
            </div>
            
            {/* Evaluation Name */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">Evaluation Name</h4>
              <input
                type="text"
                value={configChanges.name}
                onChange={(e) => handleFieldChange('name', e.target.value)}
                disabled
                className="w-full p-3 bg-gray-100 border border-gray-300 rounded-lg text-gray-600 cursor-not-allowed opacity-60"
                placeholder="Enter evaluation name"
              />
              <p className="mt-2 text-sm text-gray-600">
                The name used to identify this evaluation.
              </p>
            </div>

            {/* Evaluation Steps */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-4">Evaluation Steps</h4>
              <div className="space-y-3">
                {/* Step Upload Resume */}
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-not-allowed opacity-60">
                  <span className="text-gray-600 font-medium">Upload Resume</span>
                  <input
                    type="checkbox"
                    checked={configChanges.step_upload_resume}
                    onChange={(e) => handleFieldChange('step_upload_resume', e.target.checked)}
                    disabled
                    className="w-5 h-5 text-blue-600 rounded cursor-not-allowed"
                  />
                </label>

                {/* Step Video Intro */}
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-not-allowed opacity-60">
                  <span className="text-gray-600 font-medium">Video Introduction</span>
                  <input
                    type="checkbox"
                    checked={configChanges.step_video_intro}
                    onChange={(e) => handleFieldChange('step_video_intro', e.target.checked)}
                    disabled
                    className="w-5 h-5 text-blue-600 rounded cursor-not-allowed"
                  />
                </label>

                {/* Step Questionnaire */}
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-not-allowed opacity-60">
                  <span className="text-gray-600 font-medium">Questionnaire</span>
                  <input
                    type="checkbox"
                    checked={configChanges.step_questionnaire}
                    onChange={(e) => handleFieldChange('step_questionnaire', e.target.checked)}
                    disabled
                    className="w-5 h-5 text-blue-600 rounded cursor-not-allowed"
                  />
                </label>

                {/* Step Challenge */}
                <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-not-allowed opacity-60">
                  <span className="text-gray-600 font-medium">Challenge</span>
                  <input
                    type="checkbox"
                    checked={configChanges.step_challenge}
                    onChange={(e) => handleFieldChange('step_challenge', e.target.checked)}
                    disabled
                    className="w-5 h-5 text-blue-600 rounded cursor-not-allowed"
                  />
                </label>
              </div>
            </div>

            {/* Is Default (Toggle) */}
            <div className="mb-6">
              <h4 className="text-lg font-semibold text-gray-800 mb-3">Default Evaluation</h4>
              <label className="flex items-center justify-between p-3 bg-gray-50 rounded-lg cursor-not-allowed opacity-60">
                <span className="text-gray-600 font-medium">Set as Default</span>
                <input
                  type="checkbox"
                  checked={configChanges.is_default}
                  onChange={(e) => handleFieldChange('is_default', e.target.checked)}
                  disabled
                  className="w-5 h-5 text-blue-600 rounded cursor-not-allowed"
                />
              </label>
              <p className="mt-2 text-sm text-gray-600">
                Default evaluations are used as templates for new evaluations.
              </p>
            </div>

            {/* Save Configuration Button */}
            <div>
              <button
                disabled
                className="w-full px-6 py-3 bg-gray-400 text-white font-semibold rounded-lg cursor-not-allowed transition-colors flex items-center justify-center opacity-60"
              >
                Save Configuration (Not Implemented)
              </button>
            </div>
          </div>

          {/* Danger Zone */}
          <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-red-600 mb-4">Danger Zone</h3>
            
            <div>
              <button
                onClick={handleDeleteEvaluation}
                disabled={loading.delete}
                className="w-full px-6 py-3 bg-red-600 text-white font-semibold rounded-lg hover:bg-red-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors flex items-center justify-center"
              >
                {loading.delete ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Deleting...
                  </>
                ) : (
                  'Delete Evaluation'
                )}
              </button>
              <p className="mt-2 text-sm text-gray-600">
                This will permanently delete the evaluation. This action cannot be undone.
              </p>
            </div>
          </div>
        </div>

        {/* Right Column - Information */}
        <div>
          <div className="bg-white shadow-md rounded-lg border border-gray-200 p-6">
            <h3 className="text-xl font-bold text-gray-900 mb-6">Evaluation Information</h3>
            
            <div className="space-y-6">
              {/* Created Date (Read-only) */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Created</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-900 font-medium">
                    {evaluationData?.created_at 
                      ? new Date(evaluationData.created_at).toLocaleString()
                      : 'N/A'}
                  </div>
                </div>
              </div>


              {/* Type (Read-only) */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Type</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-900 font-medium">
                    {evaluationData?.type || 'N/A'}
                  </div>
                </div>
              </div>              

              {/* Evaluation Role (Read-only) */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Evaluation Role</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-sm text-gray-600 mb-1">Main Category</div>
                  <div className="text-gray-900 font-medium mb-3">
                    {evaluationData?.evaluation_role?.mainCategory || 'N/A'}
                  </div>
                  <div className="text-sm text-gray-600 mb-1">Sub Category</div>
                  <div className="text-gray-900 font-medium">
                    {evaluationData?.evaluation_role?.subCategory || 'N/A'}
                  </div>
                </div>
              </div>

              {/* Evaluation ID (Read-only) */}
              <div>
                <h4 className="text-lg font-semibold text-gray-800 mb-3">Evaluation ID</h4>
                <div className="bg-gray-50 rounded-lg p-4">
                  <div className="text-gray-900 font-mono text-sm break-all">
                    {evaluationId || 'N/A'}
                  </div>
                </div>
              </div>

            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EvaluationSettings;
